// Typed API client (plan.md G18; services/api/src/error.ts §10 contract). Every
// request attaches `Authorization: Bearer <jwt>` + `X-Tenant-Id` (services/api's
// middleware/auth.ts contract) and every error response's `{error:{code,message,
// details}}` envelope is parsed into a thrown `ApiRequestError` instead of a bare
// fetch Response — callers (hooks/useApi.ts) never touch `Response` directly.
//
// This module deliberately has NO React dependency: auth state lives in
// AuthContext (a React context, since it drives re-renders), but the fetch wrapper
// itself must be callable from anywhere (including outside components, e.g. a
// future non-hook caller). `configureApiAuth` is the seam between the two —
// AuthProvider calls it whenever the token/active tenant changes.

/** Mirrors services/api/src/error.ts's `ErrorEnvelope['error']` shape. */
export interface ApiErrorShape {
  code: string;
  message: string;
  details?: unknown;
}

/** Thrown for every non-2xx response. `code` is the stable machine-readable value
 * from the envelope (e.g. 'VALIDATION_ERROR', 'FORBIDDEN') — prefer switching on
 * `code`, not `message` (message is for display only). */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, shape: ApiErrorShape) {
    super(shape.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = shape.code;
    this.details = shape.details;
  }
}

export interface AuthHeaderSource {
  token: string | null;
  tenantId: string | null;
}

type AuthHeaderProvider = () => AuthHeaderSource;

let authHeaderProvider: AuthHeaderProvider = () => ({ token: null, tenantId: null });

/** Wired once by AuthProvider (src/auth/AuthContext.tsx) so this module always
 * reflects current auth state without importing React context machinery itself. */
export function configureApiAuth(provider: AuthHeaderProvider): void {
  authHeaderProvider = provider;
}

/** Fires on every 401 response, from every call site (queries AND mutations
 * alike funnel through `apiFetch`) — the one choke point that catches a
 * dead/expired token regardless of which screen or hook made the request. Wired
 * once by main.tsx's `AppRouter` to src/auth/sessionExpiry.ts's
 * redirect-to-sign-in handling, same seam pattern as `configureApiAuth` above. */
let unauthorizedHandler: (() => void) | null = null;

export function configureUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler;
}

/** Falls back to the local API dev server (services/api/src/index.ts default PORT
 * 3001) so the app is runnable with zero env configuration. */
const DEFAULT_BASE_URL = 'http://localhost:3001/api/v1';

function baseUrl(): string {
  return import.meta.env.VITE_API_URL ?? DEFAULT_BASE_URL;
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiFetchOptions {
  method?: HttpMethod;
  body?: unknown;
  signal?: AbortSignal;
  /** Extra query params, appended + URL-encoded onto the path. */
  query?: Record<string, string | number | boolean | undefined>;
}

function buildUrl(path: string, query?: ApiFetchOptions['query']): string {
  const url = new URL(path.replace(/^\//, ''), `${baseUrl().replace(/\/$/, '')}/`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/**
 * Core fetch wrapper. Parses the standard `{error:{code,message,details}}` envelope
 * on failure (services/api/src/error.ts's `onError`) and throws `ApiRequestError`;
 * on success, returns the parsed JSON body typed as `T` (callers own the shape,
 * ideally the `z.infer` type of a `@zebra/core` schema once a module ships one).
 */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { token, tenantId } = authHeaderProvider();
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  if (tenantId) headers['X-Tenant-Id'] = tenantId;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  } catch (cause) {
    throw new ApiRequestError(0, {
      code: 'NETWORK_ERROR',
      message: cause instanceof Error ? cause.message : 'Network request failed',
    });
  }

  const contentType = response.headers.get('content-type') ?? '';
  const body: unknown = contentType.includes('application/json')
    ? await response.json().catch(() => undefined)
    : undefined;

  if (!response.ok) {
    if (response.status === 401) unauthorizedHandler?.();
    const envelope = body as { error?: ApiErrorShape } | undefined;
    if (envelope?.error) {
      throw new ApiRequestError(response.status, envelope.error);
    }
    throw new ApiRequestError(response.status, {
      code: 'HTTP_ERROR',
      message: response.statusText || `Request failed with status ${response.status}`,
    });
  }

  return body as T;
}
